import type { IconName } from "../../shared/ui/Icon";

// Illustrative mock data for the hero phone and the pending stack (landing v4 prototype). Names,
// amounts and dates are decorative, not real customers. Words that are part of the app UI
// (statuses, "N/M oy", "Keyingi to‘lov"…) come from i18n `hero.mock.*` / `hero.stack.*`;
// only product names and short dates that differ per language are localized here.

export type Lang = "uz" | "ru";
type Loc = string | Record<Lang, string>;

export const loc = (v: Loc, lang: Lang): string => (typeof v === "string" ? v : v[lang]);

const NB = " ";
const usd = (n: string) => `$${n.replace(/ /g, NB)}`;

export type GridTone = "overdue" | "pending" | "active" | "draft" | "closed";

export interface GridCard {
  tone: GridTone;
  name: string;
  item: Loc;
  icon: IconName;
  /** paid / total, shown as "$450 / $1 350" with a progress bar (absent on drafts). */
  paid?: string;
  total?: string;
  pct?: number;
  /** months (or draft stages) done / total */
  n: number;
  m: number;
  /** Overdue: days late. */
  late?: number;
  /** Footer value: "$150 · 28.09" or a date. */
  due?: string;
}

const KVARTIRA: Loc = { uz: "2 xonali kvartira", ru: "2-комнатная квартира" };

export const GRID: GridCard[] = [
  { tone: "overdue", name: "Malika Rahimova", item: "iPhone 15 Pro", icon: "i-device", paid: usd("450"), total: usd("1 350"), pct: 33, n: 3, m: 9, late: 6, due: `${usd("150")} · 28.09` },
  { tone: "overdue", name: "Bekzod Tursunov", item: "Damas", icon: "i-car", paid: usd("2 000"), total: usd("7 200"), pct: 28, n: 5, m: 18, late: 2, due: `${usd("400")} · 03.10` },
  { tone: "pending", name: "Otabek Yuldashev", item: "Galaxy S25", icon: "i-device", paid: usd("0"), total: usd("1 440"), pct: 0, n: 0, m: 12 },
  { tone: "active", name: "Sardor Valiyev", item: "Chevrolet Cobalt", icon: "i-car", paid: usd("4 200"), total: usd("9 600"), pct: 44, n: 7, m: 16, due: `${usd("600")} · 05.10` },
  { tone: "active", name: "Dilnoza Karimova", item: KVARTIRA, icon: "i-house", paid: usd("18 500"), total: usd("42 000"), pct: 44, n: 11, m: 24, due: `${usd("1 125")} · 15.10` },
  { tone: "active", name: "Kamola Ergasheva", item: { uz: "Konditsioner", ru: "Кондиционер" }, icon: "i-wrench", paid: usd("300"), total: usd("900"), pct: 33, n: 2, m: 6, due: `${usd("150")} · 22.10` },
  { tone: "draft", name: "Jasur Aliyev", item: "MacBook Air M3", icon: "i-device", n: 2, m: 4 },
  { tone: "closed", name: "Nodira Saidova", item: "Redmi Note 13", icon: "i-device", paid: usd("360"), total: usd("360"), pct: 100, n: 6, m: 6, due: "02.09.2026" },
  { tone: "active", name: "Aziz Qodirov", item: { uz: "Artel muzlatgich", ru: "Холодильник Artel" }, icon: "i-device", paid: usd("240"), total: usd("720"), pct: 33, n: 4, m: 12, due: `${usd("60")} · 25.10` },
  { tone: "active", name: "Shahzod Nazarov", item: "Chevrolet Tracker", icon: "i-car", paid: usd("6 300"), total: usd("21 600"), pct: 29, n: 7, m: 24, due: `${usd("900")} · 28.10` },
];

/** in = payment arrived (accept), over = overdue (remind), up = upcoming (remind). */
export type StackKind = "in" | "over" | "up";

export interface StackItem {
  name: string;
  ini: string;
  product: Loc;
  /** "N/M oy" after the product, when it is an instalment. */
  months?: [number, number];
  /** Time label; "yesterday" → i18n `hero.stack.yesterday`. */
  time: Loc | "yesterday";
  amount: Loc;
  kind: StackKind;
  /** Days late (over) or days until (up). */
  days?: number;
}

export const STACK: StackItem[] = [
  { name: "Doniyor Rashidov", ini: "DR", product: "Chevrolet Cobalt", months: [7, 16], time: "14:20", amount: { uz: `850${NB}000 so‘m`, ru: `850${NB}000 сум` }, kind: "in" },
  { name: "Malika Rahimova", ini: "MR", product: "iPhone 15 Pro", months: [3, 9], time: { uz: "27 sen", ru: "27 сен" }, amount: usd("150"), kind: "over", days: 6 },
  { name: "Dilnoza Karimova", ini: "DK", product: KVARTIRA, time: "yesterday", amount: usd("1 125"), kind: "in" },
  { name: "Otabek Yuldashev", ini: "OY", product: "Galaxy S25", months: [1, 12], time: { uz: "5 okt", ru: "5 окт" }, amount: usd("120"), kind: "up", days: 2 },
];

/** Toast shown before the first cycle (the prototype's static "+ $600"). */
export const TOAST_FIRST_AMOUNT = usd("600");
